import React, { useState, useEffect } from 'react';
import CustomSnackbar from '../../components/CustomSnackbar';
import { PaymentTransferDetailsProps } from '../types/Payment';
import { useTranslations } from '../../localization/useTranslations';
import { getLocaleCurrencyInfo } from '../../utils/getCurrencyInfo';

const PaymentTransferDetails: React.FC<PaymentTransferDetailsProps> = ({ amount, vs, iban, account_number, swift }) => {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const translations = useTranslations();
  const { symbol: currencySymbol } = getLocaleCurrencyInfo();

  const handleCopyToClipboard = (value: string) => {
    const textarea = document.createElement('textarea');
    textarea.value = value;
    document.body.appendChild(textarea);

    textarea.select();
    document.execCommand('copy');

    document.body.removeChild(textarea);

    setSnackbarMessage(
      translations?.paymentTransferDetails?.transferDetailsCopyToClipboard ||
        'The text has been copied to the clipboard.'
    );
    setSnackbarOpen(true);
  };

  const onCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  useEffect(() => {
    if (snackbarOpen) {
      setTimeout(() => {
        setSnackbarOpen(false);
      }, 6000);
    }
  }, [snackbarOpen]);

  const getQrCodeSrc = (country: string) => {
    switch (country) {
      case 'sk':
        return `${window.crmUrl || process.env.REACT_APP_CRM_BASE_URI}/payments/qr-code/${vs}`;
      case 'cz':
        return `${window.crmUrl || process.env.REACT_APP_CRM_BASE_URI}/qr.php?amount=${amount}&vs=${vs}`;
      default:
        return `${process.env.REACT_APP_CRM_BASE_URI}/qr.php?amount=${amount}&vs=${vs}`;
    }
  };

  const currentCountry = window.country.toLowerCase() || 'cz';

  return (
    <div className="section section--details">
      <CustomSnackbar message={snackbarMessage} open={snackbarOpen} onClose={() => onCloseSnackbar()} />
      <h4 className="section__title">{translations?.paymentTransferDetails?.transferDetailsTitle || 'Details'}</h4>

      <div className="section__wrapper">
        <div className="section__box section__box--copy">
          <div className="section__content">
            <h5 className="section__subtitle">
              {translations?.paymentTransferDetails?.transferDetailsAmount || 'Amount'}
            </h5>

            <div className="section__label">
              {amount} {currencySymbol}
            </div>
          </div>
          <button className="section__button" onClick={() => handleCopyToClipboard(`${amount}`)} />
        </div>

        <div className="section__box section__box--copy">
          <div className="section__content">
            <h5 className="section__subtitle">
              {translations?.paymentTransferDetails?.transferDetailsVariableSymbol || 'Variable symbol'}
            </h5>
            <div className="section__label">{vs}</div>
          </div>
          <button className="section__button" onClick={() => handleCopyToClipboard(`${vs}`)} />
        </div>

        <div className="section__box section__box--copy">
          <div className="section__content">
            <h5 className="section__subtitle">
              {translations?.paymentTransferDetails?.transferDetailsComment || 'Comment'}
            </h5>
            <div className="section__label">VS{vs}</div>
          </div>
          <button className="section__button" onClick={() => handleCopyToClipboard(`VS${vs}`)} />
        </div>
      </div>

      <div className="section__wrapper section__wrapper--border">
        <div className="section__box section__box--copy">
          <div className="section__content">
            <h5 className="section__subtitle">{translations?.paymentTransferDetails?.transferDetailsIban || 'IBAN'}</h5>
            <div className="section__label">{iban}</div>
          </div>
          <button className="section__button" onClick={() => handleCopyToClipboard(`${iban}`)} />
        </div>

        <div className="section__box section__box--copy">
          <div className="section__content">
            <h5 className="section__subtitle">
              {translations?.paymentTransferDetails?.transferDetailsAccountNumber || 'Account number'}
            </h5>
            <div className="section__label">{account_number}</div>
          </div>
          <button className="section__button" onClick={() => handleCopyToClipboard(`${account_number}`)} />
        </div>

        <div className="section__box section__box--copy">
          <div className="section__content">
            <h5 className="section__subtitle">
              {translations?.paymentTransferDetails?.transferDetailsSwift || 'Swift'}
            </h5>
            <div className="section__label">{swift}</div>
          </div>
          <button className="section__button" onClick={() => handleCopyToClipboard(`${swift}`)} />
        </div>
      </div>

      <div id="bankTransferQrCode">
        <img id="qrCodeImage" alt="QR Code" src={getQrCodeSrc(currentCountry)} />
      </div>
    </div>
  );
};

export default PaymentTransferDetails;
